import { ReactiveVar } from '@apollo/client';
import { useReactiveVar } from '@apollo/client/react';
import { SESSION_STORAGE_KEYS } from '@fdc/constants/session-storage-keys.constants';
import { Dispatch, SetStateAction, useEffect } from 'react';

import { sessionStorageService } from '@contentservice/shared/services/storage/session-storage.service';

type SessionStorageKey = (typeof SESSION_STORAGE_KEYS)[number];

/**
 * A custom hook that functions like `useState` but persists the state
 * to `sessionStorage`, allowing it to be retained across page reloads.
 *
 * @param key The key to use when storing the value in sessionStorage.
 * @param initialValue The initial value to use if no value is found in sessionStorage.
 * @returns A stateful value, and a function to update it.
 */
export function useGlobalPersistentState<T>(
  key: SessionStorageKey,
  reactiveVar: ReactiveVar<T>
): [T, Dispatch<SetStateAction<T>>, () => void] {
  const storedValue = useReactiveVar<T>(reactiveVar);

  // sessionStorage access via useEffect to prevent SSR hydration error
  useEffect(() => {
    const item = sessionStorageService.get(key);
    if (item) {
      let parsedItem;
      try {
        parsedItem = JSON.parse(item);
      } catch {
        return;
      }
      reactiveVar(parsedItem);
    }
  }, [key]);

  const setValue: Dispatch<SetStateAction<T>> = (value) => {
    const valueToStore = value instanceof Function ? value(storedValue) : value;

    reactiveVar(valueToStore);
    sessionStorageService.set(key, JSON.stringify(valueToStore));
  };

  const clearValue = () => {
    sessionStorageService.remove(key);
  };

  return [storedValue, setValue, clearValue];
}

export default useGlobalPersistentState;
